/**
 * Die Auswahl eines Linebound.
 *
 * Ein eigenes Bauteil, weil dieselbe Auswahl an drei Stellen gebraucht wird:
 * fuer den eigenen Linebound, fuer den des Testgegners - und ab Schritt 2 des
 * Umbaus (13.10.2026) in der Lobby, wo sie eigentlich hingehoert.
 *
 * "Linebound" ist der Name aus der Lore: Wer eine Verbindung mit einem Anchor
 * ueberlebt hat und sie formt (GAME_DESIGN Abschnitt 1). Im Code heisst
 * dasselbe weiterhin "hero".
 */

import { useState } from 'react';
import { HEROES, isPlayableHero } from '@dueline/game-data';
import { t } from '../i18n/index.js';
import { LineboundSteckbrief } from './LineboundSteckbrief.js';
import { LINEBOUND_BILDER } from './menueBilder.js';

interface Props {
  /** Welcher gerade gewaehlt ist. Leer = die Zufallswahl, falls erlaubt. */
  gewaehlt: string;
  onWaehle: (heroId: string) => void;
  /** Sperrt die Auswahl, z. B. waehrend eine Verbindung aufgebaut wird. */
  gesperrt?: boolean;
  /**
   * Eine zusaetzliche Kachel "Zufall" voranstellen.
   *
   * Nur fuer den Testgegner: Beim eigenen Linebound soll man sich
   * entscheiden - gewuerfelt wird erst, wenn in der Lobby die Zeit ablaeuft.
   */
  mitZufall?: boolean;
}

export function LineboundWahl({ gewaehlt, onWaehle, gesperrt = false, mitZufall = false }: Props) {
  /*
   * Welcher Steckbrief gerade offen ist (15.10.2026).
   *
   * Er liegt HIER und nicht beim Aufrufer: Dann bekommt ihn jede Stelle,
   * an der Linebounds gewaehlt werden - Lobby, Singleplayer und die
   * Bot-Auswahl -, ohne dass jemand ihn durchreichen muss.
   */
  const [steckbrief, setSteckbrief] = useState<string | null>(null);

  return (
    <>
      <div className="dl-linebounds">
        {mitZufall && (
          <button
            type="button"
            className={
              'dl-linebound dl-linebound--bild dl-linebound--zufall' +
              (gewaehlt === '' ? ' dl-linebound--active' : '')
            }
            title={t.menu.botHeroRandomHint}
            disabled={gesperrt}
            aria-pressed={gewaehlt === ''}
            onClick={() => onWaehle('')}
          >
            {/* Kein Bild: ein grosses Fragezeichen steht an seiner Stelle. */}
            <span className="dl-linebound__zeichen" aria-hidden="true">
              ?
            </span>
            <span className="dl-linebound__schild">
              <strong>{t.menu.botHeroRandom}</strong>
            </span>
          </button>
        )}

        {HEROES.map((hero) => {
          const spielbar = isPlayableHero(hero.id);
          const bild = LINEBOUND_BILDER[hero.linebound.toLowerCase()];
          const beschreibung = spielbar ? t.menu.heroRoles[hero.role] : t.menu.heroLocked;

          /*
           * Mit Bild (15.10.2026): Das Portraet fuellt die Kachel, Name und
           * Rolle liegen unten darauf. Die kurze Beschreibung steht vorerst nur
           * noch als Hinweis beim Darueberfahren - wo die Infos zu einem
           * Linebound kuenftig stehen, ist noch offen.
           */
          if (bild) {
            return (
              <div key={hero.id} className="dl-linebound__huelle">
                <button
                  type="button"
                  className={
                    'dl-linebound dl-linebound--bild' +
                    (hero.id === gewaehlt ? ' dl-linebound--active' : '')
                  }
                  title={beschreibung}
                  disabled={!spielbar || gesperrt}
                  aria-pressed={hero.id === gewaehlt}
                  onClick={() => onWaehle(hero.id)}
                >
                  <picture className="dl-linebound__bild">
                    <source type="image/avif" srcSet={bild.avif} sizes="120px" />
                    <source type="image/webp" srcSet={bild.webp} sizes="120px" />
                    <img src={bild.ersatz} alt="" loading="lazy" decoding="async" />
                  </picture>
                  <span className="dl-linebound__schild">
                    <strong>{hero.linebound}</strong>
                    <span className="dl-linebound__rolle">{hero.name}</span>
                  </span>
                </button>
                {spielbar && (
                  <InfoKnopf name={hero.linebound} onOeffne={() => setSteckbrief(hero.id)} />
                )}
              </div>
            );
          }

          return (
            <div key={hero.id} className="dl-linebound__huelle">
              <button
                type="button"
                className={'dl-linebound' + (hero.id === gewaehlt ? ' dl-linebound--active' : '')}
                disabled={!spielbar || gesperrt}
                aria-pressed={hero.id === gewaehlt}
                onClick={() => onWaehle(hero.id)}
              >
                {/*
              Beide Namen: Der Linebound ist die Figur, der Heldenname die
              Rolle im Spiel. Auf der Homepage steht es genauso.
            */}
                <strong>{hero.linebound}</strong>
                <span className="dl-linebound__rolle">{hero.name}</span>
                <span>{spielbar ? t.menu.heroRoles[hero.role] : t.menu.heroLocked}</span>
              </button>
              {spielbar && (
                <InfoKnopf name={hero.linebound} onOeffne={() => setSteckbrief(hero.id)} />
              )}
            </div>
          );
        })}
      </div>

      {/*
        Der Steckbrief liegt ueber allem (Portal an document.body) und
        waehlt nichts von selbst: Wer ihn oeffnet, liest nach. Erst
        "Pick" darin waehlt - und nur, solange die Wahl offen ist.
      */}
      {steckbrief && (
        <LineboundSteckbrief
          heroId={steckbrief}
          onWaehle={
            gesperrt
              ? undefined
              : (heroId) => {
                  onWaehle(heroId);
                  setSteckbrief(null);
                }
          }
          onSchliessen={() => setSteckbrief(null)}
        />
      )}
    </>
  );
}

/**
 * Das kleine "i" auf einer Kachel.
 *
 * Es liegt NEBEN der Kachel im Markup und nur optisch darauf: Ein Knopf
 * in einem Knopf ist ungueltiges HTML. Und es ist NICHT gesperrt, wenn die
 * Wahl gesperrt ist - nachlesen darf man auch im Countdown, nur waehlen
 * nicht mehr.
 */
function InfoKnopf({ name, onOeffne }: { name: string; onOeffne: () => void }) {
  return (
    <button
      type="button"
      className="dl-linebound__info"
      title={t.steckbrief.info}
      aria-label={`${t.steckbrief.info}: ${name}`}
      onClick={onOeffne}
    >
      i
    </button>
  );
}
